import { useViewSettings } from "@/view-settings";
import { useBufferController } from "@/services/buffer-controller";
import { getUniqueColor } from "@/shared";
import { BufferSizeMap } from "@/shared/types";
import { ObservableListenerDispose, observe } from "@legendapp/state";
import { useObserve } from "@legendapp/state/react";
import dayjs from "dayjs";
import { RefObject, useRef } from "react";
import { DataSet } from "vis-data";
import { useTimelinePlayback } from "@/contexts/playback-context";
import { TimeRange } from "@/services/buffer-controller/time-ranges";
import {
  TIMELINE_ITEM_VIEW,
  TimelineGroupConstants,
} from "../constants/timeline";
import { isViteDevMode } from "@/shared/utils/env";
import { animalAvatarGenerator } from "@/shared/lib/animal-avatar";

type StartObserverFn = (
  viewMode?: "detections" | "snapshots"
) => ObservableListenerDispose;

export const useDisplayBufferPreview = ({
  groups,
  dataset,
  showSavedOnlyRef,
  debug,
  onClear,
}: {
  groups: DataSet<object, string>;
  dataset: DataSet<object, string>;
  showSavedOnlyRef: RefObject<boolean>;
  debug?: boolean;
  onClear: () => void;
}): { startObserver: StartObserverFn; refresh: () => void } => {
  const [tracksBuffer, _, detectionsBuffer, reprojectionBuffer] =
    useBufferController();
  const timelinePlayback = useTimelinePlayback();
  const viewSettings = useViewSettings();
  const disposeRef = useRef<ObservableListenerDispose>();
  const watching = useRef(false);

  const getStartTime = () => {
    const viewMode = viewSettings.viewMode.peek();
    if (viewMode === "detections") {
      return reprojectionBuffer.actualRange$.start.peek();
    }
    return tracksBuffer.actualRange$.start.peek();
  };

  const drawBufferChunks = (bufferChunks: BufferSizeMap) => {
    const dateRangeEnd = timelinePlayback.dateRange.end.peek();
    const startTime = getStartTime();
    const trackIds = Object.keys(bufferChunks);

    if (!startTime || !trackIds.length) return;

    const startTimeAnchor = dayjs(startTime).millisecond(0);

    const groupItems: object[] = [];
    const timelineItems: object[] = [];
    trackIds.forEach((trackId) => {
      const chunks = bufferChunks[trackId];
      const animalAvatar = animalAvatarGenerator.getRandomAvatar(trackId);
      const color = animalAvatar?.colorHex || "#ff0000";
      const group = groups.get(trackId);

      if (debug) {
        console.log(trackId, chunks);
      }

      if (!group) {
        groupItems.push({
          id: trackId,
          content: trackId,
          color,
          canSave: true,
          canHide: true,
          visible: !showSavedOnlyRef.current,
        });
      }

      chunks.map((chunk, index) => {
        const [chunkStart = 0, chunkEnd = 0] = chunk;

        const startOffset = startTimeAnchor.add(chunkStart * 1000);
        let endOffset = startTimeAnchor.add(chunkEnd * 1000 + 1000);

        // Buffer end date should never exceed date range end date
        if (dateRangeEnd && endOffset.isAfter(dateRangeEnd)) {
          endOffset = dayjs(dateRangeEnd);
        }

        timelineItems.push({
          id: `${trackId}_${index}`,
          view: TIMELINE_ITEM_VIEW.TRACK,
          className: TIMELINE_ITEM_VIEW.TRACK,
          content: `${trackId}_${index}`,
          start: startOffset.toDate(),
          end: endOffset.toDate(),
          group: trackId,
          color,
        });
      });
    });

    groups.update(groupItems);
    dataset.update(timelineItems);
  };

  const refresh = () => {
    const viewMode = viewSettings.viewMode.peek();

    if (viewMode === "detections") {
      const trackCacheChunks = reprojectionBuffer.tracksBufferSizeMap.peek();
      drawBufferChunks(trackCacheChunks);

      if (debug) {
        console.log("-----------------");
      }
    } else {
      const trackCacheChunks = tracksBuffer.tracksBufferSizeMap.peek();
      drawBufferChunks(trackCacheChunks);

      if (debug) {
        console.log("-----------------");
      }
    }
  };

  const startObserver = (
    viewMode?: "detections" | "snapshots"
  ): ObservableListenerDispose => {
    watching.current = true;

    let dispose: () => void;

    if (viewMode === "detections") {
      dispose = observe(() => {
        const projectionCacheChunks =
          reprojectionBuffer.tracksBufferSizeMap.get();
        const detectionsCacheChunks =
          detectionsBuffer.tracksBufferSizeMap.get();
        drawBufferChunks(projectionCacheChunks);
        drawBufferChunks(detectionsCacheChunks);

        if (debug) {
          console.log("-----------------");
        }
      });
    } else {
      dispose = observe(() => {
        const trackCacheChunks = tracksBuffer.tracksBufferSizeMap.get();

        drawBufferChunks(trackCacheChunks);

        if (debug) {
          console.log("-----------------");
        }
      });
    }

    if (isViteDevMode()) {
      observe(tracksBuffer.timeRanges.timeRanges$, ({ value }) => {
        const timeRanges = value;

        if (!timeRanges?.length) return;

        const { bufferStart, bufferEnd } = generateBufferAsGradient(timeRanges);

        groups.update({
          id: TimelineGroupConstants.TimeRange,
          content: "TimeRanges",
          color: getUniqueColor("TimeRanges").string,
          canSave: true,
          canHide: true,
        });
        dataset.update({
          id: TimelineGroupConstants.TimeRange,
          start: bufferStart,
          end: bufferEnd,
          content: "TimeRanges",
          view: TIMELINE_ITEM_VIEW.TRACK,
          color: getUniqueColor("TimeRanges").string,
          className: TIMELINE_ITEM_VIEW.TRACK,
          parentGroup: TimelineGroupConstants.TimeRange,
          group: TimelineGroupConstants.TimeRange,
        });
      });
    }

    disposeRef.current = dispose;

    return () => {
      watching.current = false;
      dispose();
    };
  };

  useObserve(() => {
    const viewMode = viewSettings.viewMode.get();

    if (disposeRef.current) {
      disposeRef.current?.();
    }

    onClear();
    disposeRef.current = startObserver(viewMode);
  });

  return { startObserver, refresh };
};

function generateBufferAsGradient(timeRanges: (TimeRange | undefined)[]) {
  const bufferStart = timeRanges.at(0)?.range[0] || 0;
  const bufferEnd = timeRanges.at(-1)?.range[1] || 0;
  const colors = [];

  let lastPercent = 0;

  for (let i = 0; i < timeRanges.length; i++) {
    const range = timeRanges[i];

    if (!range) continue;

    let nextPercent = 0;

    nextPercent =
      ((range.range[1] - range.range[0]) / (bufferEnd - bufferStart)) * 100;

    if (range.isEmpty) {
      colors.push(`#ccc ${lastPercent}%, #ccc ${lastPercent + nextPercent}%`);
    } else {
      colors.push(`red ${lastPercent}%, red ${lastPercent + nextPercent}%`);
    }

    lastPercent += nextPercent;

    if (timeRanges[i + 1] && timeRanges[i + 1]?.range[0] !== range.range[1]) {
      nextPercent =
        ((Number(timeRanges[i + 1]?.range[0]) - range.range[1]) /
          (bufferEnd - bufferStart)) *
        100;

      colors.push(
        `transparent ${lastPercent}%, transparent ${lastPercent + nextPercent}%`
      );

      lastPercent += nextPercent;
    }
  }

  const bgColor = `linear-gradient(0.25turn, ${colors.join(", ")})`;

  return { bgColor, bufferStart, bufferEnd };
}
